import React from "react";
import { motion } from "framer-motion";
import {
  Factory,
  Drill,
  ShieldCheck,
  Wrench,
  Activity,
  Anchor,
  Construction,
  Flame,
  ArrowUpRight,
  Droplets,
  Waves,
  Users,
  Truck,
  Building2,
  ShieldAlert,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/assets";

const easeOut = [0.16, 1, 0.3, 1];

// Icons for the practices (7 total)
const practiceIcons = [Factory, Drill, Anchor, Activity, Construction, Wrench, Flame];
const practiceBgs = [
  IMAGES.service_eng,
  IMAGES.service_drill,
  IMAGES.service_offshore || IMAGES.service_eng,
  IMAGES.service_inspection,
  IMAGES.service_crane || IMAGES.service_gear,
  IMAGES.service_fabrication,
  IMAGES.service_hse,
];

// Icons for complementary services
const extraIcons = [ShieldCheck, Droplets, Waves, Users, Truck, Building2];

export default function Services() {
  const { t } = useLang();
  const items = t.services.items;
  const extras = t.services.extras || [];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden"
    >
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{t.services.eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.75rem)] text-white tracking-tight leading-[1.05] max-w-[22ch]">
              {t.services.title}
            </h2>
          </div>
          <p className="text-[#A1A1AA] max-w-md text-base leading-relaxed">
            {t.services.subtitle}
          </p>
        </div>

        {/* Main bento: 6 practices in a clean 3x2 grid + Practice G as featured full-width card */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {items.map((item, i) => {
            const Icon = practiceIcons[i] || Factory;
            const bg = practiceBgs[i] || IMAGES.service_eng;
            const isFeatured = i === 6; // Practice G — full width

            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: 0.05 * i, ease: easeOut }}
                data-testid={`service-card-${i}`}
                className={`relative group overflow-hidden bg-[#121214] border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-500 flex flex-col ${
                  isFeatured
                    ? "md:col-span-2 lg:col-span-3 min-h-[320px]"
                    : "min-h-[360px]"
                }`}
              >
                {/* bg image */}
                <div className="absolute inset-0">
                  <img
                    src={bg}
                    alt={item.name}
                    className="w-full h-full object-cover opacity-20 transition-all duration-700 group-hover:opacity-30 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/90 to-[#0A0A0B]/40" />
                </div>

                {/* sheen on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 shine pointer-events-none" />

                {/* content */}
                <div className="relative h-full p-7 flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 border border-[#D4AF37]/40 bg-black/40 flex items-center justify-center backdrop-blur-sm">
                        <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.6} />
                      </div>
                      <span className="overline">{`Practice ${item.tag}`}</span>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-[#8B939C] opacity-0 group-hover:opacity-100 group-hover:text-[#D4AF37] transition-all duration-500 -translate-y-1 group-hover:translate-y-0" />
                  </div>

                  <div className="mt-7">
                    <h3 className="font-display font-bold text-white text-xl lg:text-2xl tracking-tight leading-tight max-w-[18ch]">
                      {item.name}
                    </h3>
                    <p className="mt-3 text-[#A1A1AA] text-sm leading-relaxed max-w-md">
                      {item.desc}
                    </p>
                    <ul className="mt-5 space-y-1.5">
                      {item.bullets.map((b, bi) => (
                        <li
                          key={bi}
                          className="text-[11px] text-[#8B939C] flex items-center gap-2 uppercase tracking-wider"
                        >
                          <span className="w-1 h-1 bg-[#D4AF37] flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Complementary services strip */}
        {extras.length > 0 && (
          <div className="mt-20 lg:mt-28">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-[#D4AF37]" />
                  <span className="overline">{t.services.extras_eyebrow}</span>
                </div>
                <h3 className="font-display font-bold text-white text-[clamp(1.5rem,2.5vw,2.25rem)] tracking-tight leading-tight max-w-[22ch]">
                  {t.services.extras_title}
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
              {extras.map((ex, i) => {
                const ExIcon = extraIcons[i] || ShieldAlert;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.05, ease: easeOut }}
                    data-testid={`service-extra-${i}`}
                    className="bg-[#0F0F11] p-8 flex items-start gap-4 hover:bg-[#121214] transition-colors duration-300 group"
                  >
                    <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37] transition-colors duration-300">
                      <ExIcon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-white text-base tracking-tight">
                        {ex.name}
                      </div>
                      <div className="mt-2 text-xs text-[#8B939C] leading-relaxed">
                        {ex.desc}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
